import {
  type Edge,
  type WorkflowNode,
  type WorkflowNodeCatalog,
} from "../model";

export const workflowDraftStorageKey = "owl.workflow.draft";

export async function loadWorkflowNodeCatalog() {
  const response = await fetch("/api/workflow/nodes");
  if (!response.ok) throw new Error("Unable to load workflow node catalog");
  return (await response.json()) as WorkflowNodeCatalog;
}

export type WorkflowDraft = { nodes: WorkflowNode[]; edges: Edge[] };

export type WorkflowRuntimeStatus = {
  state: "stopped" | "running" | "error";
  updated_at: string;
  error?: string;
  nodes: Array<{
    node_id: string;
    kind: string;
    state: string;
    error?: string;
    activity: number;
  }>;
};

export type WorkflowNodeLog = {
  timestamp: string;
  level: "info" | "error" | "warning";
  message: string;
};

export type WorkflowDepthData = {
  min: number;
  max: number;
  mean: number;
  updated_at: string;
};

export type WorkflowNodeMetrics = {
  foreground_pixels: number;
  background_pixels: number;
  foreground_ratio: number;
  updated_at: string;
};

export async function getWorkflowNodeMetrics(nodeID: string) {
  const response = await fetch(`/api/workflow/runtime/outputs/${encodeURIComponent(nodeID)}/metrics`);
  if (!response.ok) throw new Error('Unable to load node metrics');
  return (await response.json()) as WorkflowNodeMetrics;
}

export async function getWorkflowDepth(nodeID: string) {
  const response = await fetch(
    `/api/workflow/runtime/outputs/${encodeURIComponent(nodeID)}/depth`,
  );
  if (!response.ok) throw new Error("Unable to load depth data");
  return (await response.json()) as WorkflowDepthData;
}

export async function getWorkflowRuntime() {
  const response = await fetch("/api/workflow/runtime");
  if (!response.ok) throw new Error("Unable to load workflow runtime");
  return (await response.json()) as WorkflowRuntimeStatus;
}

export async function startWorkflowRuntime() {
  const response = await fetch("/api/workflow/runtime/start", {
    method: "POST",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(payload.error ?? "Unable to start workflow runtime");
  }
}

export async function stopWorkflowRuntime() {
  const response = await fetch("/api/workflow/runtime/stop", {
    method: "POST",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error ?? "Unable to stop workflow runtime");
  }
}

export async function getWorkflowNodeLogs(nodeID: string) {
  const response = await fetch(
    `/api/workflow/runtime/nodes/${encodeURIComponent(nodeID)}/logs`,
  );
  if (!response.ok) throw new Error("Unable to load node logs");
  return ((await response.json()) as { logs?: WorkflowNodeLog[] }).logs ?? [];
}

export async function applyWorkflowNode(
  nodeID: string,
  kind: string,
  config: Record<string, string>,
) {
  const response = await fetch(
    `/api/workflow/runtime/nodes/${encodeURIComponent(nodeID)}/apply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind, config }),
    },
  );
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    logs?: WorkflowNodeLog[];
  };
  return {
    logs: payload.logs ?? [],
    error: response.ok ? undefined : (payload.error ?? "Node apply failed"),
  };
}

export async function probeRTSPFeed(
  source: string,
  username = "",
  password = "",
) {
  const response = await fetch("/api/workflow/rtsp/probe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source, username, password }),
  });
  if (!response.ok) throw new Error("Unable to read RTSP feed resolution");
  return (await response.json()) as {
    width: number;
    height: number;
    resolution: string;
  };
}

export type WorkflowFeedCamera = {
  id: string;
  title: string;
  location: string;
  protocol?: string;
  streamURL: string;
};

export async function loadWorkflowFromDatabase() {
  const response = await fetch("/api/workflow");
  if (!response.ok) throw new Error("Unable to load workflow");
  return (await response.json()) as WorkflowDraft;
}

export async function saveWorkflowToDatabase(draft: WorkflowDraft) {
  const response = await fetch("/api/workflow", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(draft),
  });
  if (!response.ok) throw workflowRequestError(response, "Unable to save workflow");
}

async function workflowRequestError(response: Response, fallback: string) {
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (response.status === 401) return new Error("Session expired. Sign in again.");
  return new Error(payload.error ?? fallback);
}

export function loadWorkflowDraft(): WorkflowDraft | null {
  if (typeof window === "undefined") return null;

  try {
    const draft = JSON.parse(
      localStorage.getItem(workflowDraftStorageKey) ?? "null",
    ) as Partial<WorkflowDraft> | null;
    if (
      !draft?.nodes?.length ||
      !draft.edges
    )
      return null;
    return { nodes: draft.nodes, edges: draft.edges };
  } catch {
    return null;
  }
}

export function loadWorkflowFeedCameras(): WorkflowFeedCamera[] {
  const draft = loadWorkflowDraft();
  if (!draft) return [];

  const feeds = draft.nodes.filter((node) => node.kind === "feed");
  const incoming = new Map<string, string[]>();
  for (const edge of draft.edges) {
    incoming.set(edge.target, [
      ...(incoming.get(edge.target) ?? []),
      edge.source,
    ]);
  }

  return draft.nodes
    .filter((node) => node.kind === "camera")
    .filter((camera) =>
      feeds.some((feed) => canReachFeed(camera.id, feed.id, incoming)),
    )
    .map((camera) => ({
      id: camera.id.replace(/^camera-/, "cam-"),
      title: camera.title,
      location: camera.config.location || camera.title,
      protocol: protocolFromSource(camera.config.source),
      streamURL: `/api/cameras/${camera.id.replace(/^camera-/, "cam-")}/stream`,
    }));
}

function protocolFromSource(source?: string) {
  if (!source) return undefined;
  try {
    return new URL(source).protocol.replace(":", "");
  } catch {
    return undefined;
  }
}

function canReachFeed(
  cameraID: string,
  feedID: string,
  incoming: Map<string, string[]>,
) {
  const visited = new Set<string>();
  const pending = [feedID];

  while (pending.length) {
    const current = pending.pop()!;
    if (current === cameraID) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    pending.push(...(incoming.get(current) ?? []));
  }

  return false;
}
